import { useEffect, useState, useRef, useCallback } from "react"
import { GoogleMap, MarkerF, useJsApiLoader } from "@react-google-maps/api"
import {
    Box,
    TextField,
    IconButton,
    Tooltip,
    InputAdornment,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    CircularProgress,
} from "@mui/material"
import SearchIcon from "@mui/icons-material/Search"

import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined"
import MapOutlinedIcon from "@mui/icons-material/MapOutlined"
import SatelliteAltOutlinedIcon from "@mui/icons-material/SatelliteAltOutlined"
import FullscreenIcon from "@mui/icons-material/Fullscreen"
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit"
import AddIcon from "@mui/icons-material/Add"
import RemoveIcon from "@mui/icons-material/Remove"
import MyLocationIcon from "@mui/icons-material/MyLocation"

import { useLocalization } from "../../providers/LocalizationProvider"

const DEFAULT_CENTER = { lat: 20.5937, lng: 78.9629 }
const LIBRARIES = ["drawing", "places"]

/* ----------------------------------------
 * Reusable Map Control Button
 * -------------------------------------- */
const MapControlButton = ({ title, placement = "top", onClick, children }) => {
    const { voiceiiTags } = useLocalization()

    return (
        <Tooltip
            title={voiceiiTags(title)}
            arrow
            placement={placement}
            PopperProps={{
                container: document.querySelector(".google-map-modal-container"),
            }}
        >
            <IconButton onClick={onClick}>{children}</IconButton>
        </Tooltip>
    )
}

const LocationSelector = ({
    value,
    onChange,
    height = "300px",
    showInputs = true,
    showHomeButton = true,
    showZoomControls = true,
    showSearch = true,
}) => {
    const { voiceiiTags } = useLocalization()

    const [mapType, setMapType] = useState("roadmap")
    const [isFullscreen, setIsFullscreen] = useState(false)
    const [markerPosition, setMarkerPosition] = useState(value)

    const mapRef = useRef(null)
    const [searchQuery, setSearchQuery] = useState("")
    const [suggestions, setSuggestions] = useState([])
    const [searchLoading, setSearchLoading] = useState(false)
    const searchTimeoutRef = useRef(null)

    /* ----------------------------------------
     * Load Google Map
     * -------------------------------------- */
    const { isLoaded } = useJsApiLoader({
        id: "drawing-manager-example",
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
        libraries: LIBRARIES,
    })

    /* ----------------------------------------
     * Fullscreen listener
     * -------------------------------------- */
    useEffect(() => {
        const handler = () => setIsFullscreen(!!document.fullscreenElement)
        document.addEventListener("fullscreenchange", handler)
        return () => document.removeEventListener("fullscreenchange", handler)
    }, [])

    /* ----------------------------------------
     * Sync external value
     * -------------------------------------- */
    useEffect(() => {
        if (value?.lat && value?.lng) {
            setMarkerPosition(value)
        }
    }, [value])

    /* ----------------------------------------
     * Helpers
     * -------------------------------------- */
    const updatePosition = useCallback(
        (pos) => {
            setMarkerPosition(pos)
            onChange?.(pos)
        },
        [onChange],
    )

    const handleMapClick = (e) => {
        updatePosition(e.latLng.toJSON())
    }

    const handleSearch = async () => {
        if (!searchQuery.trim()) return
        setSearchLoading(true)
        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery.trim())}&limit=5`,
            )
            const data = await response.json()
            setSuggestions(data || [])
        } catch (error) {
            console.error("Location search failed:", error)
            setSuggestions([])
        } finally {
            setSearchLoading(false)
        }
    }

    const handleSearchInputChange = (e) => {
        const value = e.target.value
        setSearchQuery(value)
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
        if (value.trim().length >= 3) {
            searchTimeoutRef.current = setTimeout(async () => {
                setSearchLoading(true)
                try {
                    const response = await fetch(
                        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(value.trim())}&limit=5`,
                    )
                    const data = await response.json()
                    setSuggestions(data || [])
                } catch (error) {
                    console.error("Location search failed:", error)
                    setSuggestions([])
                } finally {
                    setSearchLoading(false)
                }
            }, 400)
        } else {
            setSuggestions([])
        }
    }

    const handleSelectSuggestion = (item) => {
        const pos = { lat: parseFloat(item.lat), lng: parseFloat(item.lon) }
        updatePosition(pos)
        if (mapRef.current) {
            mapRef.current.panTo(pos)
            mapRef.current.setZoom(15)
        }
        setSearchQuery(item.display_name)
        setSuggestions([])
    }

    const handleSearchKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault()
            handleSearch()
        }
    }

    const handleMapLoad = (map) => {
        mapRef.current = map
    }

    const handleHomeClick = () => {
        if (!mapRef.current || !markerPosition) return
        mapRef.current.panTo(markerPosition)
        mapRef.current.setZoom(14)
    }

    const handleZoom = (delta) => {
        if (!mapRef.current) return
        mapRef.current.setZoom(mapRef.current.getZoom() + delta)
    }

    const enterFullscreen = () => {
        document.querySelector(".google-map-modal-container")?.requestFullscreen?.()
    }

    const exitFullscreen = () => document.exitFullscreen?.()

    const handleCurrentLocation = () => {
        if (!navigator.geolocation) return
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude }
                updatePosition(loc)
                if (mapRef.current) {
                    mapRef.current.panTo(loc)
                    mapRef.current.setZoom(15)
                }
            },
            (err) => console.error("Location error:", err),
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
        )
    }

    if (!isLoaded) return null

    return (
        <>
            {/* SEARCH */}
            {showSearch && (
                <Box sx={{ mb: 1, position: "relative" }}>
                    <TextField
                    className="e-input"
                        fullWidth
                        size="small"
                        value={searchQuery}
                        onChange={handleSearchInputChange}
                        onKeyDown={handleSearchKeyDown}
                        placeholder={voiceiiTags("Search for a location")}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: "text.secondary" }} />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    {searchLoading ? (
                                        <CircularProgress size={18} />
                                    ) : (
                                        <IconButton size="small" onClick={handleSearch}>
                                            <SearchIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </InputAdornment>
                            ),
                        }}
                    />
                    {suggestions.length > 0 && (
                        <Paper
                            sx={{
                                position: "absolute",
                                zIndex: 1300,
                                width: "100%",
                                maxHeight: 200,
                                overflow: "auto",
                                mt: 0.5,
                            }}
                            elevation={3}
                        >
                            <List dense disablePadding>
                                {suggestions.map((item) => (
                                    <ListItemButton
                                        key={item.place_id}
                                        onClick={() => handleSelectSuggestion(item)}
                                    >
                                        <ListItemText
                                            primary={item.display_name}
                                            primaryTypographyProps={{
                                                noWrap: true,
                                                fontSize: "0.85rem",
                                            }}
                                        />
                                    </ListItemButton>
                                ))}
                            </List>
                        </Paper>
                    )}
                </Box>
            )}

            {/* MAP */}
            <div className="google-map-modal-container position-relative">
                <GoogleMap
                    mapContainerStyle={{
                        width: "100%",
                        height: isFullscreen ? "100vh" : height,
                    }}
                    zoom={14}
                    center={markerPosition || DEFAULT_CENTER}
                    onClick={handleMapClick}
                    onLoad={handleMapLoad}
                    options={{
                        mapTypeId: mapType,
                        zoomControl: false,
                        streetViewControl: false,
                        fullscreenControl: false,
                    }}
                >
                    {markerPosition?.lat && markerPosition?.lng && (
                        <MarkerF position={markerPosition} />
                    )}
                </GoogleMap>

                {/* MAP CONTROLS */}
                <div className="google-home-button" aria-pressed={isFullscreen}>
                    {/* TOP RIGHT */}
                    {markerPosition && (
                        <div className="map-control top-right">
                            {showHomeButton && (
                                <MapControlButton
                                    title="Go to selected location"
                                    onClick={handleHomeClick}
                                >
                                    <HomeOutlinedIcon />
                                </MapControlButton>
                            )}

                            <MapControlButton
                                title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                                onClick={isFullscreen ? exitFullscreen : enterFullscreen}
                            >
                                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                            </MapControlButton>
                        </div>
                    )}

                    {/* BOTTOM RIGHT – ZOOM */}
                    {showZoomControls && (
                        <div className="map-control bottom-right vertical">
                            <MapControlButton
                                title="Zoom in"
                                placement="left"
                                onClick={() => handleZoom(1)}
                            >
                                <AddIcon />
                            </MapControlButton>

                            <MapControlButton
                                title="Zoom out"
                                placement="left"
                                onClick={() => handleZoom(-1)}
                            >
                                <RemoveIcon />
                            </MapControlButton>

                            <MapControlButton
                                title="Current location"
                                placement="left"
                                onClick={handleCurrentLocation}
                            >
                                <MyLocationIcon />
                            </MapControlButton>
                        </div>
                    )}

                    {/* BOTTOM LEFT – MAP TYPE */}
                    <div className="map-control bottom-left">
                        <MapControlButton
                            title={mapType === "roadmap" ? "Satellite view" : "Roadmap view"}
                            onClick={() =>
                                setMapType((prev) => (prev === "roadmap" ? "satellite" : "roadmap"))
                            }
                        >
                            {mapType === "roadmap" ? (
                                <SatelliteAltOutlinedIcon />
                            ) : (
                                <MapOutlinedIcon />
                            )}
                        </MapControlButton>
                    </div>
                </div>
            </div>

            {/* LAT / LNG INPUTS */}
            {showInputs && (
                <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                    <TextField
                        label={voiceiiTags("Latitude")}
                        type="number"
                        fullWidth
                        value={markerPosition?.lat || ""}
                        onChange={(e) =>
                            updatePosition({
                                lat: Number(e.target.value),
                                lng: markerPosition?.lng,
                            })
                        }
                    />
                    <TextField
                        label={voiceiiTags("Longitude")}
                        type="number"
                        fullWidth
                        value={markerPosition?.lng || ""}
                        onChange={(e) =>
                            updatePosition({
                                lat: markerPosition?.lat,
                                lng: Number(e.target.value),
                            })
                        }
                    />
                </Box>
            )}
        </>
    )
}

export default LocationSelector
